import React from "react";
//#region types
import { DialogContentHandles, DialogProps } from "models/propTypes/dialogPropTypes";
import { CommonEnums } from "models/enums";
import { CommonConstants, FileConstants } from "utils/constants";
//#endregion
//#region components
import { Dialog, DialogContent, Grid, IconButton, Toolbar } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import useDialogOnEnter from "./useOnEnter";
import { DialogTransition as Transitions } from "../transitions";
import { ErrorBoundary } from "components/error";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import clsx from "clsx";

const FullScreenView: React.FC<DialogProps> = (props) => {
  const innerRef = React.useRef<DialogContentHandles>(null);
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);

  const onClose = () => {
    setIsDialogOpen(false);
    setTimeout(() => {
      innerRef.current?.onClose && innerRef.current.onClose();
      props.onClose && props.onClose();
      props.closeDialog && props.closeDialog();
    }, CommonConstants.DialogTransitionDurationMs);
  };

  // When confirmCloseOnX is set, the X delegates straight to props.closeDialog so the
  // caller can show a confirmation prompt without the underlying dialog auto-transitioning
  // out from under it. The caller is responsible for navigating/closing on confirm.
  const onXClose = () => {
    if (props.confirmCloseOnX && props.closeDialog) {
      props.closeDialog();
    } else {
      onClose();
    }
  };

  React.useEffect(() => {
    setIsDialogOpen(props.isOpen);
  }, [props.isOpen]);

  const childElement = React.Children.only(props.children as React.ReactElement);

  useDialogOnEnter(props.isOpen, props.onEnterHandler);

  return (
    <Dialog
      id={props.id}
      aria-labelledby="dialog-title"
      open={isDialogOpen}
      fullScreen
      classes={{
        root: clsx(cssExports.dialogRoot, props.classes?.dialogRoot),
        paperScrollPaper: clsx(cssExports.dialogScrollPaper, cssExports.fullScreenDialog, props.className),
      }}
      TransitionComponent={Transitions}
      onClose={onXClose}
    >
      <ErrorBoundary subComponentType={CommonEnums.SubComponentType.Dialog}>
        <Toolbar className={cssExports.dialogToolbar}>
          {props.title}
          <Grid item xs>
            {props.headerCustomContent}
          </Grid>
          {!props.hideCloseButton ? (
            <IconButton aria-label="close" className={cssExports.closeButton} onClick={onXClose}>
              <CloseIcon />
            </IconButton>
          ) : (
            <></>
          )}
        </Toolbar>
        <DialogContent className={cssExports.dialogContent} id={FileConstants.DialogContentFullScreenId}>
          <ErrorBoundary subComponentType={CommonEnums.SubComponentType.Dialog}>
            {React.cloneElement(childElement, {
              ref: props.setDialogRef ? innerRef : undefined,
            })}
          </ErrorBoundary>
        </DialogContent>
      </ErrorBoundary>
    </Dialog>
  );
};

export default FullScreenView;
